import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {TemplatePortal} from "@angular/cdk/portal";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Tag} from "@portal/api/contracts/tags/tag";
import {
    DeleteConfirmationDialogComponent
} from "@portal/shared/delete-confirmation-dialog/delete-confirmation-dialog.component";
import { Guid } from '@portal/api/guid';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import {TagService} from "@portal/api/tag.service";
import { NgStyle } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { MatIconButton } from "@angular/material/button";
import { MatMenuTrigger, MatMenu, MatMenuItem } from "@angular/material/menu";

@Component({
    selector: 'app-tag-selector',
    templateUrl: './tag-selector.component.html',
    styleUrls: ['./tag-selector.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgStyle, MatIcon, FormsModule, MatIconButton, CdkDropList, CdkDrag, CdkDragHandle, MatMenuTrigger, MatMenu, MatMenuItem]
})

export class TagSelectorComponent {
    @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;
    @ViewChild('tagSelectorContainer', {static: true}) tagSelectorContainer!: ElementRef;

    @Input() isMultiple = false;
    @Input() tags: Tag[] = [];
    @Input() selectedTags: Tag[] = [];

    @Output() tagAdded = new EventEmitter<Tag>();
    @Output() tagRemoved = new EventEmitter<Guid>();
    @Output() tagCreated = new EventEmitter<Tag>();
    @Output() tagUpdated = new EventEmitter<Tag>();
    @Output() tagDeleted = new EventEmitter<Guid>();
    @Output() tagsReordered = new EventEmitter<Tag[]>();
    @Output() tagColorChanged = new EventEmitter<Tag>();

    overlayRef: OverlayRef | null = null;
    editingTagId: Guid | null = null;
    newTagName: string = '';
    newTagColor: string = '#000000';
    originalTagName: string = '';

    constructor(
        private dialog: MatDialog,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef,
        private _tagsService : TagService
    ) {}

    addTag(tag: Tag) {
        if (!this.isMultiple) {
            this.tagAdded.emit(tag);
            this.closeOverlay();
        } else if (!this.selectedTags.some(t => t.id === tag.id)) {
            this.tagAdded.emit(tag);
        }
    }

    removeTag(tagId: Guid) {
        this.tagRemoved.emit(tagId);
    }

    createNewTag() {
        if (this.newTagName.trim()) {
            const newTag: Tag = {
                id: Guid.create(),
                name: this.newTagName.trim(),
                color: this.newTagColor,
                order: this.tags.length + 1
            };
            this.tagCreated.emit(newTag);
            this.newTagName = '';
            this.newTagColor = '#000000';
        }
    }

    drop(event: CdkDragDrop<Tag[]>) {
        const updatedTags = [...this.tags];
        moveItemInArray(updatedTags, event.previousIndex, event.currentIndex);
        updatedTags.forEach((tag, index) => tag.order = index + 1);
        this.tagsReordered.emit(updatedTags);
    }

    updateTagColor(tag: Tag) {
        this.tagColorChanged.emit(tag);
    }

    deleteTag(tag: Tag) {
        const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
            width: '350px',
            data: {tagName: tag.name}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this._tagsService.deleteTag(tag.id).subscribe(() => {
                    this.tagDeleted.emit(tag.id);
                    this.tags = this.tags.filter(t => t.id !== tag.id);

                });
            }
        });
    }

    startEditing(tag: Tag) {
        this.editingTagId = tag.id;
        this.originalTagName = tag.name;
    }

    updateTagName(tag: Tag): void {
        if (tag.name.trim() && tag.name !== this.originalTagName) {
            this.tagUpdated.emit(tag);
            this.editingTagId = null;
            this.originalTagName = '';
        } else {
            this.editingTagId = null;
        }
    }

    toggleDropdown(event: MouseEvent) {
        if (this.overlayRef) {
            this.closeOverlay();
            return;
        }

        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.tagSelectorContainer)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                },
            ])
            .withFlexibleDimensions(true)
            .withViewportMargin(8)
            .withPush(false);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        this.overlayRef.backdropClick().subscribe(() => {
            this.closeOverlay();
        });

        const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
        this.overlayRef.attach(portal);

        event.stopPropagation();
    }

    closeOverlay() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    isTagSelected(tagId: Guid): boolean {
        return this.selectedTags.some(tag => tag.id === tagId);
    }

    getContrastColor(bgColor: string): string {
        if (bgColor.startsWith('#')) {
            bgColor = bgColor.slice(1);
        }

        const r = parseInt(bgColor.substring(0, 2), 16);
        const g = parseInt(bgColor.substring(2, 4), 16);
        const b = parseInt(bgColor.substring(4, 6), 16);

        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        return brightness > 128 ? '#000' : '#fff';
    }
}
